import { AcpProcessSession, buildAcpDispatchSpaceActionMcpServers, createProviderOriginalError, } from "@openmeld/adapter-core";
import { createCursorAcpToolActivityTracker } from "../output/cursor-tool-activity.js";
import { readCursorPermissionOptions, resolveCursorAcpInteraction, } from "./cursor-acp-interactions.js";
const ACP_PROTOCOL_VERSION = 1;
const MAX_MODEL_DIAGNOSTIC_IDS = 100;
const MAX_MODEL_DIAGNOSTIC_ID_CHARS = 512;
const CURSOR_PLAN_UPGRADE_REQUIRED_RE = /^upgrade your plan to continue[.!]?$/iu;
export class CursorAcpTransportError extends Error {
    code;
    diagnostics;
    errorType;
    originalError;
    constructor(input) {
        super(input.message, input.cause === undefined ? undefined : { cause: input.cause });
        this.name = "CursorAcpTransportError";
        this.code = input.code;
        this.diagnostics = input.diagnostics;
        this.errorType = input.errorType;
        this.originalError = input.originalError;
    }
}
export async function invokeCursorAcpTransport(input) {
    const prompt = normalizeOptionalText(input.prompt);
    if (!prompt) {
        throw new CursorAcpTransportError({
            code: "schema.invalid",
            errorType: "config",
            message: "schema.invalid: prompt is required",
        });
    }
    const requestedReasoningLevel = normalizeOptionalText(input.conversationPolicy?.reasoningLevel);
    if (requestedReasoningLevel) {
        throw new CursorAcpTransportError({
            code: "model.reasoning_unsupported",
            errorType: "config",
            message: "model.reasoning_unsupported: Cursor ACP does not expose reasoning strength as an independent session option; choose an exact Cursor ACP model variant instead",
        });
    }
    const invokedAtMs = Date.now();
    return await withCursorAcpSession({
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        cursorTimeoutMs: input.cursorTimeoutMs,
        cursorWorkdir: input.cursorWorkdir,
        env: input.env,
        launchContract: input.launchContract,
        onInteractionRequest: input.onInteractionRequest,
        onPreview: input.onPreview,
        onProcessControlEvent: input.onProcessControlEvent,
        onRuntimeEvent: input.onRuntimeEvent,
        streamingMode: input.streamingMode,
    }, (session) => runCursorAcpTurn({
        input,
        invokedAtMs,
        prompt,
        session,
    }));
}
async function runCursorAcpTurn(input) {
    await input.session.initialize();
    const existingSessionId = normalizeOptionalText(input.input.sessionId);
    const initialDescriptor = await input.session.openSession({
        sessionId: existingSessionId,
    });
    input.session.emitProviderConversationObserved(initialDescriptor.sessionId);
    const modelResolution = await resolveCursorSessionModel({
        descriptor: initialDescriptor,
        model: normalizeOptionalText(input.input.conversationPolicy?.model),
        session: input.session,
    });
    const { descriptor } = modelResolution;
    input.session.recordDispatchDiagnostics({
        modelNegotiation: modelResolution.diagnostics,
    });
    await applyCursorSessionMode({
        descriptor,
        permissionMode: input.input.agentControllerPermissionMode,
        session: input.session,
    });
    await applyCursorSessionModel({
        descriptor,
        diagnostics: modelResolution.diagnostics,
        model: modelResolution.diagnostics.requestedModelId,
        session: input.session,
    });
    const unregisterActiveTurn = input.input.onActiveTurn?.({
        agentControllerPermissionMode: resolveCursorPermissionMode(input.input.agentControllerPermissionMode),
        cancel: () => {
            input.session.notify("session/cancel", {
                sessionId: descriptor.sessionId,
            });
            return Promise.resolve();
        },
        setPermissionMode: async (permissionMode) => {
            await input.session.request("session/set_mode", {
                modeId: resolveCursorAcpMode(permissionMode),
                sessionId: descriptor.sessionId,
            });
        },
        sessionId: descriptor.sessionId,
        taskId: input.input.taskId,
    });
    let promptResult;
    input.session.beginTurn(descriptor.sessionId);
    try {
        promptResult = await input.session.request("session/prompt", {
            prompt: [{ text: input.prompt, type: "text" }],
            sessionId: descriptor.sessionId,
        });
    }
    finally {
        input.session.endTurn();
        unregisterActiveTurn?.();
    }
    const interactionFailure = input.session.readInteractionFailure();
    if (interactionFailure) {
        throw new CursorAcpTransportError({
            code: interactionFailure.code,
            errorType: "config",
            message: interactionFailure.message,
        });
    }
    assertSuccessfulStopReason(promptResult);
    const output = input.session.readCollectedAgentText();
    await emitFinalPreviewIfRequested({
        onPreview: input.input.onPreview,
        output,
        streamingMode: input.input.streamingMode,
    });
    return {
        diagnostics: {
            modelNegotiation: modelResolution.diagnostics,
        },
        output,
        sessionId: descriptor.sessionId,
        telemetry: input.session.buildTelemetry({
            invokedAtMs: input.invokedAtMs,
            previewEmitted: input.input.streamingMode === "stream" &&
                input.input.onPreview !== undefined,
            resumedContext: existingSessionId !== null,
            streamingMode: input.input.streamingMode ?? "final",
        }),
    };
}
async function resolveCursorSessionModel(input) {
    let descriptor = input.descriptor;
    let checkAttempt = 1;
    if (input.model && !isCompleteModelInventory(descriptor)) {
        descriptor = await input.session.openSession({
            sessionId: descriptor.sessionId,
        });
        checkAttempt = 2;
    }
    const diagnostics = buildModelNegotiationDiagnostics({
        checkAttempt,
        descriptor,
        requestedModelId: input.model,
    });
    const dispatchDiagnostics = { modelNegotiation: diagnostics };
    if (input.model && !isCompleteModelInventory(descriptor)) {
        throw new CursorAcpTransportError({
            code: "model.inventory_unavailable",
            diagnostics: dispatchDiagnostics,
            errorType: "adapter",
            message: "model.inventory_unavailable: Cursor ACP did not provide one complete, consistent model inventory after a bounded recheck",
        });
    }
    if (input.model && !descriptor.availableModelIds.includes(input.model)) {
        throw new CursorAcpTransportError({
            code: "model.unavailable",
            diagnostics: dispatchDiagnostics,
            errorType: "config",
            message: `model.unavailable: Cursor ACP did not offer model '${input.model}' for this account`,
        });
    }
    return { descriptor, diagnostics };
}
async function applyCursorSessionMode(input) {
    const requestedMode = resolveCursorAcpMode(input.permissionMode);
    if (input.descriptor.currentModeId === requestedMode) {
        return;
    }
    await input.session.request("session/set_mode", {
        modeId: requestedMode,
        sessionId: input.descriptor.sessionId,
    });
}
async function applyCursorSessionModel(input) {
    const model = input.model;
    if (!model) {
        return;
    }
    if (model === input.descriptor.currentModelId) {
        return;
    }
    try {
        await input.session.request("session/set_config_option", {
            configId: "model",
            sessionId: input.descriptor.sessionId,
            type: "id",
            value: model,
        });
    }
    catch (error) {
        if (error instanceof CursorAcpTransportError &&
            error.code === "protocol.method_not_found") {
            await applyLegacyCursorSessionModel({ ...input, model });
            return;
        }
        if (error instanceof CursorAcpTransportError &&
            error.code === "protocol.invalid_params") {
            throw new CursorAcpTransportError({
                code: "model.unavailable",
                diagnostics: { modelNegotiation: input.diagnostics },
                errorType: "config",
                message: `model.unavailable: Cursor ACP rejected model '${model}' for this session`,
                ...(error.originalError ? { originalError: error.originalError } : {}),
            });
        }
        throw error;
    }
}
async function applyLegacyCursorSessionModel(input) {
    try {
        await input.session.request("session/set_model", {
            modelId: input.model,
            sessionId: input.descriptor.sessionId,
        });
    }
    catch (error) {
        if (error instanceof CursorAcpTransportError &&
            error.code === "protocol.invalid_params") {
            throw new CursorAcpTransportError({
                code: "model.unavailable",
                diagnostics: { modelNegotiation: input.diagnostics },
                errorType: "config",
                message: `model.unavailable: Cursor ACP rejected model '${input.model}' for this session`,
                ...(error.originalError ? { originalError: error.originalError } : {}),
            });
        }
        throw error;
    }
}
async function withCursorAcpSession(input, callback) {
    const session = new CursorAcpSession(input);
    try {
        session.start();
        return await callback(session);
    }
    catch (error) {
        throw toCursorAcpTransportError(error, session.readStderr(), session.readDispatchDiagnostics());
    }
    finally {
        await session.close();
    }
}
class CursorAcpSession {
    #agentControllerPermissionMode;
    #cursorWorkdir;
    #mcpServers;
    #onInteractionRequest;
    #onPreview;
    #onRuntimeEvent;
    #process;
    #streamingMode;
    #toolActivityTracker = createCursorAcpToolActivityTracker();
    #dispatchDiagnostics = null;
    #firstOutputAtMs = null;
    #interactionFailure = null;
    #output = "";
    #turnActive = false;
    #activeSessionId = null;
    constructor(input) {
        this.#agentControllerPermissionMode = input.agentControllerPermissionMode;
        this.#cursorWorkdir = input.cursorWorkdir;
        this.#mcpServers = buildAcpDispatchSpaceActionMcpServers(input.env);
        this.#onInteractionRequest = input.onInteractionRequest;
        this.#onPreview = input.onPreview;
        this.#onRuntimeEvent = input.onRuntimeEvent;
        this.#streamingMode = input.streamingMode ?? "final";
        this.#process = new AcpProcessSession({
            args: buildCursorAcpArgs(input.agentControllerPermissionMode),
            createError: (error) => new CursorAcpTransportError(error),
            cwd: input.cursorWorkdir,
            ...(input.env ? { env: input.env } : {}),
            launchContract: input.launchContract,
            mapJsonRpcError: toJsonRpcTransportError,
            onClientRequest: (request) => this.#handleClientRequest(request),
            onLifecycleEvent: (event) => this.#emitRuntimeEvent(event),
            onNotification: (notification) => this.#handleNotification(notification),
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            processLabel: "cursor-agent",
            timeoutMs: input.cursorTimeoutMs,
        });
    }
    start() {
        this.#process.start();
    }
    async initialize() {
        const initializeResult = toRecord(await this.request("initialize", {
            clientCapabilities: {
                fs: {
                    readTextFile: false,
                    writeTextFile: false,
                },
            },
            clientInfo: {
                name: "OpenMeld",
                version: "0.0.0",
            },
            protocolVersion: ACP_PROTOCOL_VERSION,
        }));
        if (initializeResult?.protocolVersion !== ACP_PROTOCOL_VERSION) {
            throw new CursorAcpTransportError({
                code: "protocol.invalid",
                errorType: "adapter",
                message: `protocol.invalid: Cursor ACP negotiated unsupported protocol version '${String(initializeResult?.protocolVersion ?? "missing")}'`,
            });
        }
        const supportsCursorLogin = Array.isArray(initializeResult.authMethods) &&
            initializeResult.authMethods.some((authMethod) => toRecord(authMethod)?.id === "cursor_login");
        if (!supportsCursorLogin) {
            throw new CursorAcpTransportError({
                code: "protocol.invalid",
                errorType: "adapter",
                message: "protocol.invalid: Cursor ACP did not offer the required cursor_login authentication method",
            });
        }
        await this.request("authenticate", {
            methodId: "cursor_login",
        });
    }
    async openSession(input) {
        const params = {
            cwd: this.#cursorWorkdir,
            mcpServers: this.#mcpServers,
        };
        if (input.sessionId) {
            let result;
            try {
                result = await this.request("session/load", {
                    ...params,
                    sessionId: input.sessionId,
                });
            }
            catch (error) {
                if (error instanceof CursorAcpTransportError &&
                    error.code === "protocol.invalid_params") {
                    throw new CursorAcpTransportError({
                        cause: error,
                        code: "context_not_found",
                        errorType: "adapter",
                        message: "context_not_found: Cursor ACP could not load the persisted conversation",
                        ...(error.originalError
                            ? { originalError: error.originalError }
                            : {}),
                    });
                }
                throw error;
            }
            return readSessionDescriptor(result, input.sessionId, "load");
        }
        const result = await this.request("session/new", params);
        const record = toRecord(result);
        const sessionId = normalizeOptionalText(record?.sessionId);
        if (!sessionId) {
            throw new CursorAcpTransportError({
                code: "protocol.invalid",
                errorType: "adapter",
                message: "protocol.invalid: Cursor ACP session/new response omitted sessionId",
            });
        }
        return readSessionDescriptor(result, sessionId, "new");
    }
    async close() {
        await this.#process.close();
    }
    async request(method, params) {
        return await this.#process.request(method, params);
    }
    notify(method, params) {
        this.#process.notify(method, params);
    }
    beginTurn(sessionId) {
        this.#activeSessionId = sessionId;
        this.#firstOutputAtMs = null;
        this.#output = "";
        this.#turnActive = true;
    }
    endTurn() {
        this.#turnActive = false;
        this.#activeSessionId = null;
    }
    readCollectedAgentText() {
        return this.#output.trim();
    }
    readDispatchDiagnostics() {
        return this.#dispatchDiagnostics;
    }
    readInteractionFailure() {
        return this.#interactionFailure;
    }
    readStderr() {
        return this.#process.readStderr();
    }
    recordDispatchDiagnostics(diagnostics) {
        this.#dispatchDiagnostics = diagnostics;
    }
    buildTelemetry(input) {
        return this.#process.buildTelemetry({
            ...input,
            firstOutputAtMs: this.#firstOutputAtMs,
        });
    }
    emitProviderConversationObserved(sessionId) {
        this.#emitRuntimeEvent({
            agentControllerConversationId: sessionId,
            phase: "provider_conversation_observed",
            tsMs: Date.now(),
        });
    }
    #handleNotification(message) {
        if (!this.#turnActive) {
            return;
        }
        this.#collectAgentMessageChunk(message);
        const toolActivity = this.#toolActivityTracker.observe(message);
        if (toolActivity) {
            this.#emitRuntimeEvent(toolActivity);
        }
    }
    async #handleClientRequest(message) {
        if (message.method === "session/request_permission" &&
            resolveCursorPermissionMode(this.#agentControllerPermissionMode) ===
                "cursor:force") {
            return this.#resolvePermissionRequest(message.params);
        }
        if (this.#onInteractionRequest && message.method) {
            const interaction = await resolveCursorAcpInteraction({
                method: message.method,
                onInteractionRequest: this.#onInteractionRequest,
                params: message.params,
            });
            if (interaction.handled) {
                return { result: interaction.result };
            }
        }
        if (message.method === "session/request_permission") {
            return this.#resolvePermissionRequest(message.params);
        }
        if (message.method === "cursor/ask_question") {
            this.#interactionFailure ??= {
                code: "interaction.question_required",
                message: "interaction.question_required: Cursor requested user input, which OpenMeld phase 1 does not support",
            };
            return {
                result: {
                    outcome: {
                        outcome: "skipped",
                        reason: "OpenMeld does not support interactive Cursor questions yet.",
                    },
                },
            };
        }
        if (message.method === "cursor/create_plan") {
            this.#interactionFailure ??= {
                code: "interaction.plan_required",
                message: "interaction.plan_required: Cursor requested plan approval, which OpenMeld phase 1 does not support",
            };
            return {
                result: {
                    outcome: {
                        outcome: "rejected",
                        reason: "OpenMeld does not support interactive Cursor plan approval yet.",
                    },
                },
            };
        }
        this.#interactionFailure ??= {
            code: "interaction.unsupported",
            message: `interaction.unsupported: Cursor ACP requested unsupported client method '${message.method}'`,
        };
        return {
            error: {
                code: -32_601,
                message: `OpenMeld does not support Cursor ACP client method '${message.method}'.`,
            },
        };
    }
    #resolvePermissionRequest(params) {
        const options = readCursorPermissionOptions(params);
        const forceMode = resolveCursorPermissionMode(this.#agentControllerPermissionMode) ===
            "cursor:force";
        const selectedOption = forceMode
            ? options.find((option) => option.kind === "allow_once")
            : options.find((option) => option.kind === "reject_once");
        if (!(forceMode && selectedOption?.kind === "allow_once")) {
            this.#interactionFailure ??= {
                code: "interaction.permission_required",
                message: "interaction.permission_required: Cursor requested permission that the selected OpenMeld mode did not grant",
            };
        }
        return {
            result: selectedOption
                ? {
                    outcome: {
                        optionId: selectedOption.optionId,
                        outcome: "selected",
                    },
                }
                : {
                    outcome: {
                        outcome: "cancelled",
                    },
                },
        };
    }
    #collectAgentMessageChunk(message) {
        if (message.method !== "session/update") {
            return;
        }
        const params = toRecord(message.params);
        const update = toRecord(params?.update);
        if (update?.sessionUpdate === "agent_thought_chunk") {
            if (params?.sessionId !== this.#activeSessionId) {
                return;
            }
            const thought = toRecord(update.content);
            if (thought?.type === "text" && typeof thought.text === "string") {
                this.#emitRuntimeEvent({
                    phase: "reasoning",
                    blockId: String(params?.sessionId),
                    text: thought.text,
                    mode: "append",
                    tsMs: Date.now(),
                });
            }
            return;
        }
        if (update?.sessionUpdate !== "agent_message_chunk") {
            return;
        }
        const content = toRecord(update.content);
        const text = content?.type === "text" ? normalizeOutputText(content.text) : null;
        if (!text) {
            return;
        }
        if (this.#firstOutputAtMs === null) {
            this.#firstOutputAtMs = Date.now();
        }
        this.#output += text;
        if (this.#streamingMode === "stream" && this.#onPreview) {
            Promise.resolve(this.#onPreview({ text: this.#output })).catch((error) => {
                this.#process.fail(error);
            });
        }
    }
    #emitRuntimeEvent(event) {
        this.#onRuntimeEvent?.(event);
    }
}
function readSessionDescriptor(value, sessionId, sessionOperation) {
    const record = toRecord(value);
    const modes = toRecord(record?.modes);
    const models = toRecord(record?.models);
    const modelConfig = readConfigOption(record, "model");
    const legacyAvailableModelIds = readLegacyAvailableModelIds(models);
    const configAvailableModelIds = readConfigAvailableModelIds(modelConfig);
    const legacyCurrentModelId = normalizeOptionalText(models?.currentModelId);
    const configCurrentModelId = normalizeOptionalText(modelConfig?.currentValue);
    const modelInventory = resolveModelInventory({
        configAvailableModelIds,
        configCurrentModelId,
        configOptionPresent: modelConfig !== null,
        legacyAvailableModelIds,
        legacyCurrentModelId,
    });
    return {
        availableModelIds: modelInventory.availableModelIds,
        configAvailableModelIds,
        configCurrentModelId,
        currentModeId: normalizeOptionalText(modes?.currentModeId) ??
            readCurrentConfigValue(record, "mode"),
        currentModelId: modelInventory.currentModelId,
        legacyAvailableModelIds,
        legacyCurrentModelId,
        modelSourceState: modelInventory.sourceState,
        sessionOperation,
        sessionId,
    };
}
function readLegacyAvailableModelIds(models) {
    const directModels = Array.isArray(models?.availableModels)
        ? models.availableModels
        : [];
    return uniqueModelIds(directModels.map((model) => toRecord(model)?.modelId));
}
function readConfigAvailableModelIds(modelConfig) {
    const options = Array.isArray(modelConfig?.options)
        ? modelConfig.options
        : [];
    return uniqueModelIds(options.map((option) => toRecord(option)?.value));
}
function uniqueModelIds(values) {
    return [
        ...new Set(values
            .map(normalizeOptionalText)
            .filter((modelId) => modelId !== null)),
    ];
}
function resolveModelInventory(input) {
    const currentModelId = input.configCurrentModelId ?? input.legacyCurrentModelId;
    if (input.configAvailableModelIds.length > 0) {
        const sourcesConflict = (input.legacyAvailableModelIds.length > 0 &&
            !haveSameModelIds(input.configAvailableModelIds, input.legacyAvailableModelIds)) ||
            (input.configCurrentModelId !== null &&
                input.legacyCurrentModelId !== null &&
                input.configCurrentModelId !== input.legacyCurrentModelId) ||
            (currentModelId !== null &&
                !input.configAvailableModelIds.includes(currentModelId));
        if (sourcesConflict) {
            return {
                availableModelIds: [],
                currentModelId,
                sourceState: "conflicting",
            };
        }
        return {
            availableModelIds: input.configAvailableModelIds,
            currentModelId,
            sourceState: input.legacyAvailableModelIds.length > 0
                ? "consistent"
                : "config_options_only",
        };
    }
    if (input.configOptionPresent) {
        return {
            availableModelIds: [],
            currentModelId,
            sourceState: input.legacyAvailableModelIds.length > 0 ? "conflicting" : "missing",
        };
    }
    if (input.legacyAvailableModelIds.length > 0) {
        return {
            availableModelIds: input.legacyAvailableModelIds,
            currentModelId: input.legacyCurrentModelId,
            sourceState: input.legacyCurrentModelId !== null &&
                !input.legacyAvailableModelIds.includes(input.legacyCurrentModelId)
                ? "conflicting"
                : "legacy_models_only",
        };
    }
    return {
        availableModelIds: [],
        currentModelId,
        sourceState: "missing",
    };
}
function haveSameModelIds(left, right) {
    if (left.length !== right.length) {
        return false;
    }
    const rightIds = new Set(right);
    return left.every((modelId) => rightIds.has(modelId));
}
function isCompleteModelInventory(descriptor) {
    return (descriptor.modelSourceState !== "conflicting" &&
        descriptor.modelSourceState !== "missing");
}
function buildModelNegotiationDiagnostics(input) {
    const configAvailableModelIds = sanitizeDiagnosticModelIds(input.descriptor.configAvailableModelIds);
    const legacyAvailableModelIds = sanitizeDiagnosticModelIds(input.descriptor.legacyAvailableModelIds);
    return {
        checkAttempt: input.checkAttempt,
        configAvailableModelCount: input.descriptor.configAvailableModelIds.length,
        configAvailableModelIds,
        configAvailableModelIdsTruncated: wereDiagnosticModelIdsTruncated(input.descriptor.configAvailableModelIds),
        configCurrentModelId: sanitizeDiagnosticModelId(input.descriptor.configCurrentModelId),
        currentModelId: sanitizeDiagnosticModelId(input.descriptor.currentModelId),
        legacyAvailableModelCount: input.descriptor.legacyAvailableModelIds.length,
        legacyAvailableModelIds,
        legacyAvailableModelIdsTruncated: wereDiagnosticModelIdsTruncated(input.descriptor.legacyAvailableModelIds),
        legacyCurrentModelId: sanitizeDiagnosticModelId(input.descriptor.legacyCurrentModelId),
        protocol: "acp/v1",
        requestedModelId: sanitizeDiagnosticModelId(input.requestedModelId),
        requestedPresentInConfigOptions: input.requestedModelId !== null &&
            input.descriptor.configAvailableModelIds.includes(input.requestedModelId),
        requestedPresentInLegacyModels: input.requestedModelId !== null &&
            input.descriptor.legacyAvailableModelIds.includes(input.requestedModelId),
        sessionOperation: input.descriptor.sessionOperation,
        sourceState: input.descriptor.modelSourceState,
    };
}
function sanitizeDiagnosticModelIds(modelIds) {
    return modelIds
        .slice(0, MAX_MODEL_DIAGNOSTIC_IDS)
        .map((modelId) => sanitizeDiagnosticModelId(modelId))
        .filter((modelId) => modelId !== null);
}
function wereDiagnosticModelIdsTruncated(modelIds) {
    return (modelIds.length > MAX_MODEL_DIAGNOSTIC_IDS ||
        modelIds.some((modelId) => modelId.length > MAX_MODEL_DIAGNOSTIC_ID_CHARS));
}
function sanitizeDiagnosticModelId(modelId) {
    return modelId === null
        ? null
        : modelId.slice(0, MAX_MODEL_DIAGNOSTIC_ID_CHARS);
}
function readCurrentConfigValue(record, id) {
    return normalizeOptionalText(readConfigOption(record, id)?.currentValue);
}
function readConfigOption(record, id) {
    const configOptions = Array.isArray(record?.configOptions)
        ? record.configOptions
        : [];
    return (configOptions
        .map(toRecord)
        .find((option) => (normalizeOptionalText(option?.configId) ??
        normalizeOptionalText(option?.id)) === id) ?? null);
}
function resolveCursorAcpMode(value) {
    switch (resolveCursorPermissionMode(value)) {
        case "cursor:ask":
            return "ask";
        case "cursor:plan":
            return "plan";
        default:
            return "agent";
    }
}
function resolveCursorPermissionMode(value) {
    switch (value) {
        case "cursor:ask":
        case "cursor:autoReview":
        case "cursor:default":
        case "cursor:force":
        case "cursor:plan":
            return value;
        default:
            return "cursor:default";
    }
}
function buildCursorAcpArgs(permissionMode) {
    switch (resolveCursorPermissionMode(permissionMode)) {
        case "cursor:autoReview":
            return ["--auto-review", "acp"];
        case "cursor:force":
            return ["--force", "acp"];
        default:
            return ["acp"];
    }
}
function assertSuccessfulStopReason(value) {
    const stopReason = normalizeOptionalText(toRecord(value)?.stopReason);
    if (!stopReason) {
        throw new CursorAcpTransportError({
            code: "protocol.invalid",
            errorType: "adapter",
            message: "protocol.invalid: Cursor ACP session/prompt omitted stopReason",
        });
    }
    if (stopReason === "end_turn") {
        return;
    }
    throw new CursorAcpTransportError({
        code: `runtime.turn.${stopReason}`,
        errorType: "adapter",
        message: `runtime.turn.${stopReason}: Cursor ACP ended the turn with stopReason '${stopReason}'`,
    });
}
function isCursorPlanUpgradeRequired(value) {
    return CURSOR_PLAN_UPGRADE_REQUIRED_RE.test(value.trim());
}
async function emitFinalPreviewIfRequested(input) {
    if (input.streamingMode === "stream" || !input.onPreview) {
        return;
    }
    await input.onPreview({ text: input.output });
}
function toJsonRpcTransportError(error) {
    const message = normalizeOptionalText(error.message) ?? "Cursor ACP error";
    const originalError = createProviderOriginalError(message);
    if (error.code === -32_601) {
        return new CursorAcpTransportError({
            code: "protocol.method_not_found",
            errorType: "adapter",
            message,
            ...(originalError ? { originalError } : {}),
        });
    }
    if (error.code === -32_602) {
        return new CursorAcpTransportError({
            code: "protocol.invalid_params",
            errorType: "config",
            message,
            ...(originalError ? { originalError } : {}),
        });
    }
    return classifyCursorFailure(message, originalError);
}
function toCursorAcpTransportError(error, stderr, diagnostics) {
    if (error instanceof CursorAcpTransportError) {
        return error.diagnostics || !diagnostics
            ? error
            : new CursorAcpTransportError({
                code: error.code,
                diagnostics,
                errorType: error.errorType,
                message: error.message,
                ...(error.originalError
                    ? { originalError: error.originalError }
                    : {}),
            });
    }
    const combined = [toErrorMessage(error), normalizeOptionalText(stderr)]
        .filter((value) => value !== null)
        .join("\n");
    const classified = classifyCursorFailure(combined);
    return diagnostics
        ? new CursorAcpTransportError({
            code: classified.code,
            diagnostics,
            errorType: classified.errorType,
            message: classified.message,
        })
        : classified;
}
function classifyCursorFailure(message, originalError = null) {
    const normalized = normalizeOptionalText(message) ?? "Cursor ACP error";
    const lowered = normalized.toLowerCase();
    if (isCursorPlanUpgradeRequired(normalized)) {
        return new CursorAcpTransportError({
            code: "cursor.plan_upgrade_required",
            errorType: "adapter",
            message: normalized,
            ...(originalError ? { originalError } : {}),
        });
    }
    if (lowered.includes("network") ||
        lowered.includes("fetch failed") ||
        lowered.includes("connection") ||
        lowered.includes("econn") ||
        lowered.includes("dns")) {
        return new CursorAcpTransportError({
            code: "network.unavailable",
            errorType: "network",
            message: normalized,
            ...(originalError ? { originalError } : {}),
        });
    }
    if (lowered.includes("not logged in") ||
        lowered.includes("unauthorized") ||
        lowered.includes("authentication") ||
        lowered.includes("invalid api key") ||
        lowered.includes("401")) {
        return new CursorAcpTransportError({
            code: "auth.required",
            errorType: "auth",
            message: normalized,
            ...(originalError ? { originalError } : {}),
        });
    }
    if (lowered.includes("session not found") ||
        lowered.includes("unknown session")) {
        return new CursorAcpTransportError({
            code: "context_not_found",
            errorType: "config",
            message: normalized,
            ...(originalError ? { originalError } : {}),
        });
    }
    return new CursorAcpTransportError({
        code: "upstream_unreachable",
        errorType: "adapter",
        message: normalized,
        ...(originalError ? { originalError } : {}),
    });
}
function normalizeOutputText(value) {
    return typeof value === "string" && value.length > 0 ? value : null;
}
function normalizeOptionalText(value) {
    const normalized = typeof value === "string" ? value.trim() : "";
    return normalized.length > 0 ? normalized : null;
}
function toRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : null;
}
function toErrorMessage(error) {
    return error instanceof Error ? error.message : String(error);
}
